import { useState } from 'react'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useViewerRole } from '@/auth/useViewerRole'
import { ImpersonationBanner } from '@/components/ImpersonationBanner'
import { useAuth } from '@/hooks/useAuth'
import { Sidebar } from '@/navigation/Sidebar'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/shared/components/atoms/ui/dropdown-menu'

/**
 * App shell.
 *
 * Navigation is rendered by the declarative `<Sidebar />` component,
 * which reads from `packages/frontend-app/src/navigation/config.ts` and
 * filters per viewer via `filterNav` + `useViewerRole`. Non-permitted
 * items are absent from the DOM (defense in depth).
 */
export function Layout() {
	const { user, logout } = useAuth()
	const navigate = useNavigate()
	const viewer = useViewerRole()
	const [collapsed, setCollapsed] = useState(false)

	// Dev-only impersonation menu entry (ADR-0009, Spec 0 Group 11).
	// Shown only when (a) viewer is admin, (b) build is non-prod, and
	// (c) not currently already impersonating (nested impersonation is disabled
	// in favor of "Return to self → start a new one").
	const stage = import.meta.env.VITE_APP_STAGE
	const canImpersonate = viewer.isAdmin && stage !== 'prod'
	const currentlyImpersonating = Boolean(user?.impersonatedBy)

	return (
		<div className="min-h-screen flex flex-col bg-muted">
			{/* Banner self-hides when user.impersonatedBy is unset — driven by
			    the /auth/me viewer, not by browser storage. */}
			<ImpersonationBanner />
			<div className="flex flex-1 min-h-0">
				{/* Sidebar */}
				<aside
					className={`bg-white border-r border-border flex flex-col transition-all duration-300 ease-in-out ${collapsed ? 'w-0 overflow-hidden opacity-0' : 'w-56 opacity-100'}`}
				>
					{/* Logo */}
					<div className="px-4 py-4 border-b border-border">
						<NavLink to="/dashboard" className="block">
							<div className="text-lg font-bold text-foreground">KXFC</div>
							<div className="text-[10px] text-muted-foreground tracking-wide">Keenfolks X — Flight Control</div>
						</NavLink>
					</div>

					{/* Navigation — declarative, role-filtered */}
					<div className="flex-1 px-2 py-4 overflow-y-auto">
						<Sidebar />
					</div>

					{/* Version + Stage */}
					<div className="px-4 py-1 text-[9px] text-muted-foreground flex justify-between">
						<span>
							v{import.meta.env.VITE_APP_VERSION || '1.0.0'}
							{import.meta.env.VITE_APP_VERSION_HASH && ` (${import.meta.env.VITE_APP_VERSION_HASH})`}
						</span>
						<span>{import.meta.env.VITE_APP_STAGE || 'local'}</span>
					</div>

					{/* User menu at bottom */}
					<div className="border-t border-border p-3">
						{user && (
							<DropdownMenu>
								<DropdownMenuTrigger className="w-full flex items-center gap-2 px-2 py-2 rounded-md text-sm text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-gray-300 cursor-pointer">
									<div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">
										{(user.displayName || user.email).charAt(0).toUpperCase()}
									</div>
									<div className="flex-1 text-left min-w-0">
										<div className="text-sm font-medium text-foreground truncate">{user.displayName || user.email}</div>
										<div className="text-xs text-muted-foreground truncate">{user.role}</div>
									</div>
								</DropdownMenuTrigger>
								<DropdownMenuContent align="start" side="top" className="w-56">
									<DropdownMenuLabel>
										<div className="flex flex-col">
											<span className="font-medium">{user.displayName || user.email}</span>
											{user.displayName && <span className="text-xs text-muted-foreground">{user.email}</span>}
										</div>
									</DropdownMenuLabel>
									<DropdownMenuSeparator />
									<DropdownMenuItem onClick={() => void navigate('/settings')}>Settings</DropdownMenuItem>
									{canImpersonate && (
										<DropdownMenuItem
											onClick={() => {
												if (!currentlyImpersonating) void navigate('/dev/switch-user')
											}}
											disabled={currentlyImpersonating}
										>
											Impersonate user
											<span className="ml-1 text-[10px] text-muted-foreground uppercase">dev</span>
										</DropdownMenuItem>
									)}
									<DropdownMenuSeparator />
									<DropdownMenuItem onClick={() => void logout()} className="text-red-600 focus:text-red-700">
										Logout
									</DropdownMenuItem>
								</DropdownMenuContent>
							</DropdownMenu>
						)}
					</div>
				</aside>

				{/* Main content */}
				<main className="flex-1 overflow-auto">
					{/* Sidebar toggle */}
					<button
						type="button"
						onClick={() => setCollapsed((prev) => !prev)}
						className="fixed top-4 left-2 z-50 w-8 h-8 flex items-center justify-center rounded-md bg-white border border-border shadow-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-300"
						style={{ left: collapsed ? '8px' : '216px' }}
						title={collapsed ? 'Show sidebar' : 'Hide sidebar'}
					>
						<svg
							className={`w-4 h-4 transition-transform duration-300 ${collapsed ? 'rotate-180' : ''}`}
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
						</svg>
					</button>

					<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
						<Outlet />
					</div>
				</main>
			</div>
		</div>
	)
}
